import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorld, useWorldCtx } from '../data/WorldProvider';
import type { AllowanceView, QuoteView, TradeSide, TxView } from '../data/types';
import { parseUnits, short, units } from '../lib/format';
import { journey } from '../lib/journey';
import { racePresentation } from '../data/racePresentation';
import { useLaunchInfo } from '../data/launch';
import { halloweenEvent } from '../data/runDay';
import { nextRace } from './RaceChip';
import { WalletModal } from './WalletModal';

export const STAGE_TEXT: Record<TxView['stage'], string> = {
  idle: 'Ready',
  quoting: 'Getting a quote…',
  review: 'Review the quote',
  'approval-needed': 'Approval needed',
  approving: 'Approving (wallet)…',
  'signature-requested': 'Waiting for your wallet signature…',
  broadcast: 'Broadcast — waiting for the sequencer',
  'sequencer-accepted': 'Included by the sequencer (not final yet)',
  confirmed: 'Confirmed',
  failed: 'Failed',
  rejected: 'Rejected in wallet',
  replaced: 'Replaced or cancelled',
  expired: 'Quote expired',
};

/** Gas budget used for the pre-signature check (matches ChainWorld). */
const TRADE_GAS = 360_000n;
const APPROVE_GAS = 60_000n;

/** Recent wallet transactions with their honest stage (survives reloads for broadcast transactions). */
export function TxList({ txs, max = 5 }: { txs?: TxView[]; max?: number }) {
  if (!txs || txs.length === 0) return <p className="small muted">None from this browser yet.</p>;
  return (
    <ul className="tx-list" aria-label="Your recent transactions" data-testid="tx-list">
      {txs.slice(0, max).map((t) => (
        <li key={t.id} data-stage={t.stage} data-label={t.label}>
          <span className="stage">{t.label}:</span> {STAGE_TEXT[t.stage]}
          {t.hash && <span className="mono muted"> {short(t.hash)}</span>}
          {t.detail && <div className="small muted">{t.detail}</div>}
        </li>
      ))}
    </ul>
  );
}

/** Exact-input trade form with honest quote, fee, approval, gas and receipt states. Used on /trade and as the Run drawer. */
export function TradeForm({ initialSide = 'buy', compactMode = false }: { initialSide?: TradeSide; compactMode?: boolean }) {
  const { view, source } = useWorld();
  const { mode } = useWorldCtx();
  const [side, setSide] = useState<TradeSide>(initialSide);
  const [payWith, setPayWith] = useState<'quote' | 'eth'>('quote');
  const [amount, setAmount] = useState(initialSide === 'buy' ? '25' : '250000');
  const [quote, setQuote] = useState<QuoteView | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tx, setTx] = useState<TxView | null>(null);
  const [busy, setBusy] = useState(false);
  const [reviewedWindow, setReviewedWindow] = useState<number | null>(null);
  const [pickingWallet, setPickingWallet] = useState(false);
  const [allowance, setAllowance] = useState<AllowanceView | null>(null);
  const [nonce, setNonce] = useState(0);
  const inAsset = side === 'buy' ? view.quote : view.runner;
  const outAsset = side === 'buy' ? view.runner : view.quote;
  const parsed = parseUnits(amount, inAsset.decimals);
  const reqId = useRef(0);
  const live = mode !== 'demo';
  const u = view.user;
  const race = racePresentation(view);
  const launch = useLaunchInfo();
  const races = launch?.races;
  // RUN DAY has no Any-App team draw: the jackpot is the onchain Golden Shoe only.
  const runDay = !!halloweenEvent(launch);
  const next = races ? nextRace(races.times, Date.now(), races.from ? Date.parse(races.from) : 0) : null;
  const earlyDraw = !!next && next - Date.now() <= (races?.anyAppEarlyHours ?? 2) * 3_600_000;
  const drawOpen = race.kind !== 'offline' && (['live', 'resolving', 'starting'].includes(race.kind) || earlyDraw);
  const entryOpen = view.run?.shoe?.status === 'Open' && view.timing.now < view.run.shoe.cutoff;
  const evidence = tx?.influence;
  const resolved = evidence ? view.steps.find(s => s.window === evidence.window) : null;
  const ethRoute = side === 'buy' && payWith === 'eth';
  const unsupported = !view.quotePolicy.supported;

  useEffect(() => { setTx(null); setReviewedWindow(null); }, [u.address, source]);
  useEffect(() => { journey('trade_opened', { side: initialSide, mode }); }, []);
  useEffect(() => { if (quote) journey('quote_ready', { side, phase: race.kind, mode }); }, [quote]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  // Re-quote when the read API comes back after an outage (a stale error must not stick).
  const apiState = view.freshness.api === 'down' ? 'down' : 'up';

  useEffect(() => {
    setQuote(null);
    setErr(null);
    const id = ++reqId.current;
    setQuoting(false);
    if (!parsed || parsed === 0n || ethRoute || unsupported) return;
    setQuoting(true);
    const t = setTimeout(() => {
      source
        .quote({ side, amountIn: parsed })
        .then((q) => id === reqId.current && setQuote(q))
        .catch((e: Error) => id === reqId.current && setErr(e.message))
        .finally(() => id === reqId.current && setQuoting(false));
    }, 250);
    return () => { clearTimeout(t); reqId.current++; };
  }, [side, amount, source, parsed, ethRoute, unsupported, nonce, apiState]);

  // Allowance the trade will need (local chain): shown before anything is signed.
  useEffect(() => {
    setAllowance(null);
    if (!live || !u.connected || !parsed || !source.allowance || ethRoute) return;
    let stop = false;
    source
      .allowance(side, parsed)
      .then((a) => !stop && setAllowance(a))
      .catch(() => {});
    return () => {
      stop = true;
    };
  }, [live, u.connected, u.address, side, parsed, source, ethRoute, tx, nonce]);

  const balance = side === 'buy' ? u.quote : u.runner;
  const insufficient = parsed !== null && u.connected && parsed > balance;
  const expired = quote ? quote.expiresAt < view.timing.now : false;
  const gasNeed = live && u.gasPrice ? (TRADE_GAS + (allowance?.needed ? APPROVE_GAS : 0n)) * u.gasPrice : 0n;
  const noGas = live && u.connected && u.gas !== undefined && u.gasPrice !== undefined && u.gasPrice > 0n && u.gas < gasNeed;
  const stale = live && (view.freshness.api !== 'ok' || view.freshness.stale || view.freshness.catchingUp);
  const blockers: string[] = [];
  if (unsupported) blockers.push(view.quotePolicy.note);
  if (ethRoute) blockers.push(`Unsupported funding route: ETH → quote → RUNNER isn't atomic. Pay with ${view.quote.symbol}.`);
  if (live && !u.connected) blockers.push('Connect a wallet to trade.');
  if (live && u.wrongChain) blockers.push(`Wrong network: switch to ${view.network ? `${view.network.name} (chain ${view.network.id})` : 'the network this site is built for'}.`);
  if (insufficient) blockers.push(`Insufficient ${inAsset.symbol} balance (${units(balance, inAsset.decimals)} available).`);
  if (noGas) blockers.push(`Insufficient ETH for gas: need ≈ ${units(gasNeed, 18, 6)} ETH, have ${units(u.gas ?? 0n, 18, 6)} ETH.`);
  if (stale) blockers.push('Read API disconnected: quotes can’t refresh until it’s back.');

  const submit = async () => {
    if (!quote || !parsed || expired || busy || blockers.length) return;
    journey('trade_submitted', { side, phase: race.kind, mode });
    setBusy(true);
    setReviewedWindow(race.canInfluence ? view.openInput?.window ?? null : null);
    setTx({ id: 'pending', label: 'Trade', stage: live ? 'signature-requested' : 'review', at: view.timing.now });
    try {
      const r = await source.trade({ side, amountIn: parsed }, quote);
      setTx(r);
      journey('trade_result', { side, stage: r.stage, mode });
      if (r.stage === 'confirmed') refresh();
    } catch (e) {
      setTx({ id: 'err', label: 'Trade', stage: 'failed', detail: (e as Error).message, at: view.timing.now });
    } finally {
      setBusy(false);
    }
  };

  // How hard this trade steers within one 8-second window: its RUNNER quantity in ppm of the reference supply,
  // against the contract's per-window cap (300 ppm), as a percentage.
  const pushRunner = quote ? (side === 'buy' ? quote.amountOut : quote.amountIn) : 0n;
  const pushPct = quote && view.refSupply > 0n ? Math.min(100, Math.round((Number(pushRunner / (view.refSupply / 1_000_000n)) / 300) * 100)) : null;
  return (
    <div className="stack" data-testid="trade-form">
      <div className="segmented direction" role="group" aria-label="Trade direction">
        {/* Short visible label on phones; the accessible name always says which way it steers. */}
        <button aria-pressed={side === 'buy'} onClick={() => setSide('buy')} aria-label="Buy RUNNER">
          Buy RUNNER →
        </button>
        <button aria-pressed={side === 'sell'} onClick={() => setSide('sell')} aria-label="Sell RUNNER">
          ← Sell RUNNER
        </button>
      </div>
      {side === 'buy' && !compactMode && (
        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Pay with</span>
          <select className="field" value={payWith} onChange={(e) => setPayWith(e.target.value as 'quote' | 'eth')} aria-label="Pay with">
            <option value="quote">{view.quote.symbol}</option>
            <option value="eth">ETH (not supported)</option>
          </select>
        </label>
      )}
      <label className="stack" style={{ gap: 4 }}>
        <span className="small">
          You pay ({ethRoute ? 'ETH' : inAsset.symbol}) · balance {u.connected ? (ethRoute ? units(u.gas ?? 0n, 18, 4) : units(balance, inAsset.decimals)) : 'connect wallet to view'}
        </span>
        <input className="field big" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} aria-invalid={!parsed || insufficient} aria-label="Amount you pay" />
      </label>
      {!parsed && (
        <p className="small" role="alert">
          Enter an amount (max {inAsset.decimals} decimals).
        </p>
      )}
      {blockers.map((b) => (
        <p key={b} className="small blocker" role="alert" data-testid="trade-blocker">
          {b}
        </p>
      ))}
      {live && u.connected && (insufficient || noGas || u.wrongChain) && <div className="trade-funding" data-testid="funding-help"><strong>Before you trade</strong><p>{u.wrongChain ? `Switch your wallet to ${view.network?.name ?? 'the supported chain'}.` : `You need ${side === 'buy' ? view.quote.symbol : 'RUNNER'} to trade and ETH for network gas on ${view.network?.name ?? 'this chain'}.`}</p><p>Use your wallet’s supported funding or swap flow for this network. Assets on another network are not available here. Balances update automatically.</p>{u.wrongChain && source.switchChain && <button className="btn small" onClick={() => void source.switchChain?.().catch((e: Error) => setErr(e.message))}>Switch network</button>}</div>}
      {err && (
        <p className="small" role="alert" data-testid="quote-error">
          Quote unavailable: {err}
        </p>
      )}
      {quoting && !quote && !err && <p className="small muted">Getting a quote…</p>}
      {quote && (
        <div className="quote-card" aria-live="polite" data-testid="quote">
          {/* What you get and what it does lead; the fee and settlement detail is one tap away. */}
          <div className="quote-get">
            <span>You get ≈</span>
            <strong>
              {units(quote.amountOut, outAsset.decimals)} <small>{outAsset.symbol}</small>
            </strong>
            <span className="small muted">
              at least {units(quote.minOut, outAsset.decimals)} ({(quote.slippageBps / 100).toFixed(2)}% slippage)
            </span>
          </div>
          <div className="quote-chips">
            <span className="tag">
              Fees ≈ {units(quote.projectFee + quote.lpFee, view.quote.decimals, 4)} {view.quote.symbol}
            </span>
            {quote.priceImpactBps >= 100 && <span className="tag pending">Price impact {(quote.priceImpactBps / 100).toFixed(2)}%</span>}
            {live && allowance?.needed && <span className="tag pending">Approval needed first</span>}
          </div>
          <dl className="trade-eligibility" data-testid="trade-eligibility">
            <div><dt>Race influence</dt><dd>{race.canInfluence ? `${side === 'buy' ? 'Right' : 'Left'} contribution${pushPct !== null ? ` · up to ${pushPct}% of the window cap` : ''}. Other trades can offset it.` : race.kind === 'offline' ? 'Unknown until live data returns' : 'No immediate movement in this race state'}</dd></div>
            <div><dt>Golden Shoe entry</dt><dd>{race.kind === 'offline' ? 'Unknown — refresh live data' : quote.shoeEligible && entryOpen ? 'Eligible if confirmed before cutoff. Weight follows the fee paid.' : side === 'sell' ? 'No — sells do not enter' : 'No — entry window is closed'}</dd></div>
            {races?.anyAppPrize && !runDay ? <div><dt>Separate team draw</dt><dd>{side === 'buy' && drawOpen ? `Scheduled window open · net canonical-pool buys are assessed separately for the $${races.anyAppPrize} draw` : race.kind === 'offline' ? 'Eligibility unavailable' : 'This trade does not currently qualify'}<br />Team-run; not confirmed by this quote.</dd></div> : null}
          </dl>
          <details className="more">
            <summary>Fees and quote details</summary>
            <dl className="kv">
              <dt>Project fee</dt>
              <dd>
                {units(quote.projectFee, view.quote.decimals, 4)} {view.quote.symbol}
              </dd>
              <dt>LP fee ({(view.market.lpFeePips / 10_000).toFixed(2)}%)</dt>
              <dd>
                ≈ {units(quote.lpFee, view.quote.decimals, 4)} {view.quote.symbol}
              </dd>
              <dt>Price impact</dt>
              <dd>{(quote.priceImpactBps / 100).toFixed(2)}%</dd>
              {live && (
                <>
                  <dt>Approval</dt>
                  <dd data-testid="approval-state" data-needed={allowance ? String(allowance.needed) : 'unknown'}>
                    {!u.connected
                      ? 'connect a wallet'
                      : !allowance
                        ? 'checking…'
                        : allowance.needed
                          ? `needed: exactly ${units(parsed ?? 0n, inAsset.decimals)} ${inAsset.symbol}`
                          : 'sufficient'}
                  </dd>
                  <dt>Gas</dt>
                  <dd>{u.gasPrice ? `≈ ${units(gasNeed, 18, 6)} ETH` : 'paid in ETH'}</dd>
                  <dt>Quoted at</dt>
                  <dd>block {quote.sourceBlock ?? '?'}</dd>
                </>
              )}
            </dl>
          </details>
          <p className="small muted quote-valid" data-testid="quote-freshness" data-expired={expired ? 'yes' : 'no'}>
            Quote {expired ? 'expired — refresh' : `valid ~${Math.max(0, Math.ceil(quote.expiresAt - view.timing.now))}s`}
          </p>
        </div>
      )}
      {quote && expired && (
        <button className="btn small" onClick={refresh} data-testid="refresh-quote">
          Refresh quote
        </button>
      )}
      {live && !u.connected ? (
        <button className="btn primary" disabled={u.connecting} onClick={() => { journey('wallet_connect_requested', { side, mode }); setPickingWallet(true); }} data-testid="trade-connect-wallet">
          {u.connecting ? 'Connecting…' : `Connect wallet to ${side}`}
        </button>
      ) : (
        <button className={`btn ${side === 'buy' ? 'buy' : 'sell'}`} disabled={!parsed || !quote || quoting || busy || expired || blockers.length > 0} onClick={submit} data-testid="submit-trade">
          {mode === 'demo' ? `Simulate ${side}` : allowance?.needed ? `Approve, then ${side} in wallet` : `Review & ${side} in wallet`}
        </button>
      )}
      {live && u.walletError && <p className="small blocker" role="alert">{u.walletError}</p>}
      {pickingWallet && <WalletModal networkName={view.network?.name ?? 'Robinhood Chain'} onClose={() => setPickingWallet(false)} onPick={(walletId) => {
        setPickingWallet(false);
        void source.connect('injected', walletId).catch(() => {});
      }} />}
      {busy && view.txs && view.txs[0] && (
        <div className="panel" role="status" style={{ boxShadow: 'none' }} data-testid="tx-progress">
          <strong>
            {view.txs[0].label}: {STAGE_TEXT[view.txs[0].stage]}
          </strong>
          {view.txs[0].hash && <div className="small mono">{view.txs[0].hash}</div>}
        </div>
      )}
      {tx && !busy && (
        <div className="panel" role="status" style={{ boxShadow: 'none' }} data-testid="tx-result" data-stage={tx.stage}>
          <strong>{STAGE_TEXT[tx.stage]}</strong>
          {tx.detail && <div className="small">{tx.detail}</div>}
          {tx.hash && <div className="small mono">{tx.hash}</div>}
          {tx.stage === 'confirmed' && <div className="trade-outcome"><ol><li>{mode === 'demo' ? 'Simulated trade completed. No real funds moved.' : 'Trade confirmed onchain.'}</li><li>{evidence ? `Swap recorded in input window ${evidence.window}.${reviewedWindow !== null && reviewedWindow !== evidence.window ? " It arrived in a different window than previewed." : ""} ${evidence.shoeEntry ? 'A Shoe entry was recorded.' : 'No Shoe entry was recorded.'}` : 'Waiting for receipt evidence of the game input; movement is not yet verified.'}</li><li>{resolved ? `Window resolved: the shared runner moved ${Number(resolved.state.x - resolved.before.x) > 0 ? 'right' : Number(resolved.state.x - resolved.before.x) < 0 ? 'left' : 'straight ahead'}. This is the combined market result.` : 'No resolved race step is reported for this swap yet. A trade between races does not move the runner.'}</li></ol></div>}
        </div>
      )}
      {live && (
        <div>
          <h3 className="small" style={{ margin: '4px 0' }}>
            Your transactions
          </h3>
          <TxList txs={view.txs} max={compactMode ? 3 : 6} />
        </div>
      )}
      {!compactMode && (
        <p className="note">
          {mode === 'demo'
            ? 'Demo: simulated pool. Nothing is signed.'
            : 'Check recipient, pool, amounts and fees in your wallet. Approvals are exact; reloading never resends.'}
        </p>
      )}
    </div>
  );
}

export function TradeDrawer({ side, onClose }: { side: TradeSide; onClose(): void }) {
  const ref = useRef<HTMLDivElement>(null);
  // The parent re-renders on every live update with a new onClose; keep the latest in a ref so focus moves only when
  // the drawer opens and closes (re-running this on each update pulled focus out of the amount field).
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const on = (e: KeyboardEvent) => {
      if (document.querySelector('[data-testid="wallet-picker"]')) return;
      if (e.key === 'Escape') { e.preventDefault(); close.current(); }
      if (e.key === 'Tab') {
        const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), a[href], summary') ?? []).filter(el => el.checkVisibility());
        const first = items[0], last = items.at(-1);
        if (e.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    window.addEventListener('keydown', on);
    return () => {
      window.removeEventListener('keydown', on);
      prev?.focus();
    };
  }, []);
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer" role="dialog" aria-modal="true" aria-label="Trade" tabIndex={-1} ref={ref}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Trade</h2>
          <button className="btn small ghost" onClick={onClose}>
            Close
          </button>
        </div>
        <TradeForm initialSide={side} compactMode />
      </div>
    </>
  );
}
